import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  HardDrive, Download, Search, ChevronLeft, ChevronRight, 
  Calendar, Filter, X, ArrowRight, History,
  Package, Users, Shield, Settings, MemoryStick, Cpu,
  Monitor, AlertTriangle, Plus, Minus, Server, CalendarClock,
  ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateARG } from '../../lib/utils';

// ============ HELPER COMPONENTS (Outside main component) ============

// Sort icon component
const SortIcon = ({ field, sortConfig }) => {
  if (sortConfig.field !== field) {
    return <ArrowUpDown className="h-4 w-4 text-muted-foreground/50" />;
  }
  return sortConfig.dir === 'asc' 
    ? <ArrowUp className="h-4 w-4 text-primary" />
    : <ArrowDown className="h-4 w-4 text-primary" />;
};

// Change type badge
const ChangeTypeBadge = ({ type, isAlert = false }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
    type === 'added' 
      ? isAlert ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
      : 'bg-yellow-500/20 text-yellow-400'
  }`}>
    {type === 'added' ? <Plus className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
    {type === 'added' ? 'Agregado' : 'Removido'}
  </span>
);

// Filters component
const FiltersBar = ({ filters, setFilters, placeholder = "Buscar..." }) => {
  const setDateRange = (days) => {
    setFilters(prev => ({
      ...prev,
      date_from: new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      date_to: new Date().toISOString().split('T')[0]
    }));
  };

  return (
    <Card className="card-subtle">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filtros:</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <Input type="date" value={filters.date_from}
              onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
              className="h-9 w-36" />
            <span className="text-muted-foreground">a</span>
            <Input type="date" value={filters.date_to}
              onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
              className="h-9 w-36" />
          </div>
          <div className="flex items-center gap-1 border-l pl-3">
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setDateRange(7)}>
              7 días
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setDateRange(30)}>
              1 mes
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setDateRange(90)}>
              3 meses
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input placeholder={placeholder} value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="h-9 w-48" />
          </div>
          {(filters.search || filters.change_type) && (
            <Button variant="ghost" size="sm" 
              onClick={() => setFilters(prev => ({ ...prev, search: '', change_type: '' }))}>
              <X className="h-4 w-4 mr-1" /> Limpiar
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Stats cards for list-based tabs
const ListStatsCards = ({ stats, filters, setFilters }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
    <div className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${!filters.change_type ? 'ring-2 ring-primary' : ''}`}
      onClick={() => setFilters(prev => ({ ...prev, change_type: '' }))}>
      <History className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
      <div>
        <div className="stats-card-value">{stats.total_changes}</div>
        <div className="stats-card-label">Total Cambios</div>
      </div>
    </div>
    <div className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-green-500/50 ${filters.change_type === 'added' ? 'ring-2 ring-green-500' : ''}`}
      onClick={() => setFilters(prev => ({ ...prev, change_type: prev.change_type === 'added' ? '' : 'added' }))}>
      <Plus className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
      <div>
        <div className="stats-card-value text-green-500">{stats.added}</div>
        <div className="stats-card-label">Agregados</div>
      </div>
    </div>
    <div className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-yellow-500/50 ${filters.change_type === 'removed' ? 'ring-2 ring-yellow-500' : ''}`}
      onClick={() => setFilters(prev => ({ ...prev, change_type: prev.change_type === 'removed' ? '' : 'removed' }))}>
      <Minus className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={1.5} />
      <div>
        <div className="stats-card-value text-yellow-500">{stats.removed}</div>
        <div className="stats-card-label">Removidos</div>
      </div>
    </div>
    <div className="stats-card flex items-center gap-3">
      <Server className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
      <div>
        <div className="stats-card-value text-blue-500">{stats.hosts_affected}</div>
        <div className="stats-card-label">Hosts Afectados</div>
      </div>
    </div>
  </div>
);

// Pagination component
const PaginationBar = ({ data, onPageChange }) => (
  data.total > 0 ? (
    <div className="flex items-center justify-between">
      <div className="text-sm text-muted-foreground">{data.total} cambios encontrados</div>
      {data.pages > 1 && (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onPageChange(data.page - 1)} disabled={data.page === 1}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">{data.page} / {data.pages}</span>
          <Button variant="outline" size="sm" onClick={() => onPageChange(data.page + 1)} disabled={data.page === data.pages}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  ) : null
);

// Empty state
const EmptyState = ({ error, icon: Icon = History }) => (
  <div className="text-center py-12 text-muted-foreground">
    <Icon className="h-12 w-12 mx-auto mb-3 opacity-50" />
    <p>No se encontraron cambios en el período seleccionado</p>
    <p className="text-sm mt-2">{error || 'Probá ampliando el rango de fechas'}</p>
  </div>
);

// Loading spinner
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
  </div>
);

// ============ SCHEDULED TASKS TAB COMPONENT ============
const ScheduledTasksTab = () => {
  const defaultDateFrom = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const defaultDateTo = new Date().toISOString().split('T')[0];
  
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState({ total_changes: 0, added: 0, removed: 0, modified: 0, hosts_affected: 0 });
  const [changes, setChanges] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [filters, setFilters] = useState({ 
    date_from: defaultDateFrom, 
    date_to: defaultDateTo,
    search: '', 
    change_type: '' 
  });

  // Fetch stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get('/reports/traceability/scheduled-tasks/stats', {
          params: { date_from: filters.date_from, date_to: filters.date_to }
        });
        setStats(res.data);
      } catch (err) {
        console.error('Error fetching scheduled tasks stats:', err);
      }
    };
    fetchStats();
  }, [filters.date_from, filters.date_to]);

  // Fetch changes
  const fetchChanges = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await api.get('/reports/traceability/scheduled-tasks/changes', {
        params: {
          date_from: filters.date_from,
          date_to: filters.date_to,
          change_type: filters.change_type || undefined,
          search: filters.search || undefined,
          page,
          page_size: 50
        }
      });
      setChanges(res.data);
    } catch (err) {
      console.error('Error fetching scheduled tasks changes:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchChanges(1);
  }, [filters.date_from, filters.date_to, filters.change_type, filters.search]);

  // Export
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/reports/traceability/scheduled-tasks/export', {
        params: {
          date_from: filters.date_from,
          date_to: filters.date_to,
          change_type: filters.change_type || undefined
        },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trazabilidad_tareas_${filters.date_from}_${filters.date_to}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting:', err);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dt) => formatDateARG(dt);

  const getActionBadge = (action) => {
    const styles = {
      added: 'bg-green-500/10 text-green-400 border-green-500/30',
      removed: 'bg-red-500/10 text-red-400 border-red-500/30',
      modified: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'
    };
    const icons = {
      added: <Plus className="h-3 w-3 mr-1" />,
      removed: <Minus className="h-3 w-3 mr-1" />,
      modified: <ArrowRight className="h-3 w-3 mr-1" />
    };
    const labels = { added: 'Agregada', removed: 'Eliminada', modified: 'Modificada' };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs border ${styles[action]}`}>
        {icons[action]}{labels[action]}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div 
          className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${!filters.change_type ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setFilters(prev => ({ ...prev, change_type: '' }))}
        >
          <History className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
          <div>
            <div className="stats-card-value">{stats.total_changes}</div>
            <div className="stats-card-label">Total Cambios</div>
          </div>
        </div>
        <div 
          className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-green-500/50 ${filters.change_type === 'added' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setFilters(prev => ({ ...prev, change_type: prev.change_type === 'added' ? '' : 'added' }))}
        >
          <Plus className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
          <div>
            <div className="stats-card-value">{stats.added}</div>
            <div className="stats-card-label">Agregadas</div>
          </div>
        </div>
        <div 
          className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-red-500/50 ${filters.change_type === 'removed' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setFilters(prev => ({ ...prev, change_type: prev.change_type === 'removed' ? '' : 'removed' }))}
        >
          <Minus className="h-6 w-6 text-red-500 flex-shrink-0" strokeWidth={1.5} />
          <div>
            <div className="stats-card-value">{stats.removed}</div>
            <div className="stats-card-label">Eliminadas</div>
          </div>
        </div>
        <div 
          className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-yellow-500/50 ${filters.change_type === 'modified' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => setFilters(prev => ({ ...prev, change_type: prev.change_type === 'modified' ? '' : 'modified' }))}
        >
          <ArrowRight className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={1.5} />
          <div>
            <div className="stats-card-value">{stats.modified}</div>
            <div className="stats-card-label">Modificadas</div>
          </div>
        </div>
        <div className="stats-card flex items-center gap-3">
          <Server className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
          <div>
            <div className="stats-card-value">{stats.hosts_affected}</div>
            <div className="stats-card-label">Hosts Afectados</div>
          </div>
        </div>
      </div>

      {/* Filters - con controles de fecha */}
      <Card className="card-subtle">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filtros:</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Input type="date" value={filters.date_from}
                onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                className="h-9 w-36" />
              <span className="text-muted-foreground">a</span>
              <Input type="date" value={filters.date_to}
                onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                className="h-9 w-36" />
            </div>
            <div className="flex items-center gap-1 border-l pl-3">
              <Button variant="ghost" size="sm" className="h-7 text-xs px-2" 
                onClick={() => setFilters(prev => ({ 
                  ...prev, 
                  date_from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                  date_to: new Date().toISOString().split('T')[0]
                }))}>
                7 días
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs px-2"
                onClick={() => setFilters(prev => ({ 
                  ...prev, 
                  date_from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                  date_to: new Date().toISOString().split('T')[0]
                }))}>
                1 mes
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs px-2"
                onClick={() => setFilters(prev => ({ 
                  ...prev, 
                  date_from: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                  date_to: new Date().toISOString().split('T')[0]
                }))}>
                3 meses
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Buscar servidor o tarea..." 
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="h-9 w-48" 
              />
            </div>
            {(filters.search || filters.change_type) && (
              <Button variant="ghost" size="sm" onClick={() => setFilters(prev => ({ ...prev, search: '', change_type: '' }))}>
                <X className="h-4 w-4 mr-1" /> Limpiar
              </Button>
            )}
            <div className="ml-auto">
              <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
                <Download className="h-4 w-4 mr-2" />
                {exporting ? 'Exportando...' : 'Exportar'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Changes Table */}
      <Card className="card-subtle">
        <CardHeader className="border-b border-border pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-primary" />
            Cambios en Tareas Programadas
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <LoadingSpinner />
          ) : changes.items.length === 0 ? (
            <EmptyState icon={CalendarClock} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left p-3 font-medium">Servidor</th>
                    <th className="text-left p-3 font-medium">Tarea</th>
                    <th className="text-left p-3 font-medium">Acción</th>
                    <th className="text-left p-3 font-medium">Detalle</th>
                    <th className="text-left p-3 font-medium">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.items.map((item) => (
                    <tr key={item.id} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="p-3 font-mono text-xs">{item.hostname}</td>
                      <td className="p-3">
                        <div className="max-w-[200px]">
                          <div className="text-sm truncate" title={item.task_name}>{item.task_name}</div>
                          {item.task_path && item.task_path !== '\\' && (
                            <div className="text-xs text-muted-foreground truncate" title={item.task_path}>{item.task_path}</div>
                          )}
                        </div>
                      </td>
                      <td className="p-3">{getActionBadge(item.change_type)}</td>
                      <td className="p-3">
                        {item.change_type === 'modified' && item.old_value && item.new_value ? (
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-red-400 max-w-[100px] truncate" title={item.old_value}>{item.old_value}</span>
                            <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                            <span className="text-green-400 max-w-[100px] truncate" title={item.new_value}>{item.new_value}</span>
                          </div>
                        ) : item.change_type === 'added' ? (
                          <span className="text-green-400 text-xs">Nueva tarea</span>
                        ) : item.change_type === 'removed' ? (
                          <span className="text-red-400 text-xs">Tarea eliminada</span>
                        ) : '-'}
                      </td>
                      <td className="p-3 text-muted-foreground text-xs whitespace-nowrap">{formatDate(item.detected_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <PaginationBar data={changes} onPageChange={fetchChanges} />
    </div>
  );
};

// ============ MAIN COMPONENT ============

const TraceabilityReport = () => {
  const [activeTab, setActiveTab] = useState('hardware');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Overview
  const [overview, setOverview] = useState({
    hardware: { available: false, changes_week: 0 },
    software: { available: false, changes_week: 0 },
    parches: { available: false, changes_week: 0 },
    localadmin: { available: false, changes_week: 0 },
    roles: { available: false, changes_week: 0 },
    scheduled_tasks: { available: false, changes_week: 0 }
  });

  // Common date filters
  const defaultDateFrom = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const defaultDateTo = new Date().toISOString().split('T')[0];

  // ============ HARDWARE STATE ============
  const [hwStats, setHwStats] = useState({
    total_changes: 0, ram_changes: 0, os_changes: 0,
    serial_changes: 0, cores_changes: 0, storage_changes: 0
  });
  const [hwChanges, setHwChanges] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [hwFilters, setHwFilters] = useState({
    date_from: defaultDateFrom, date_to: defaultDateTo, change_type: '', search: ''
  });
  const [hwSort, setHwSort] = useState({ field: 'fecha', dir: 'desc' });

  // ============ SOFTWARE STATE ============
  const [swStats, setSwStats] = useState({ total_changes: 0, added: 0, removed: 0, hosts_affected: 0 });
  const [swChanges, setSwChanges] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [swFilters, setSwFilters] = useState({
    date_from: defaultDateFrom, date_to: defaultDateTo, change_type: '', search: ''
  });
  const [swSort, setSwSort] = useState({ field: 'fecha', dir: 'desc' });

  // ============ PARCHES STATE ============
  const [pStats, setPStats] = useState({ total_changes: 0, added: 0, removed: 0, hosts_affected: 0 });
  const [pChanges, setPChanges] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [pFilters, setPFilters] = useState({
    date_from: defaultDateFrom, date_to: defaultDateTo, change_type: '', search: ''
  });
  const [pSort, setPSort] = useState({ field: 'fecha', dir: 'desc' });

  // ============ LOCALADMIN STATE ============
  const [laStats, setLaStats] = useState({ total_changes: 0, added: 0, removed: 0, hosts_affected: 0 });
  const [laChanges, setLaChanges] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [laFilters, setLaFilters] = useState({
    date_from: defaultDateFrom, date_to: defaultDateTo, change_type: '', search: ''
  });
  const [laSort, setLaSort] = useState({ field: 'fecha', dir: 'desc' });

  // ============ ROLES STATE ============
  const [rStats, setRStats] = useState({ total_changes: 0, added: 0, removed: 0, hosts_affected: 0 });
  const [rChanges, setRChanges] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [rFilters, setRFilters] = useState({
    date_from: defaultDateFrom, date_to: defaultDateTo, change_type: '', search: ''
  });
  const [rSort, setRSort] = useState({ field: 'fecha', dir: 'desc' });

  // Fetch overview
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const response = await api.get('/reports/traceability/overview');
        setOverview(response.data);
      } catch (error) {
        console.error('Error fetching overview:', error);
      }
    };
    fetchOverview();
  }, []);

  // ============ HARDWARE FETCHERS ============
  const fetchHwStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (hwFilters.date_from) params.append('date_from', hwFilters.date_from);
      if (hwFilters.date_to) params.append('date_to', hwFilters.date_to);
      const response = await api.get(`/reports/traceability/hardware/stats?${params}`);
      setHwStats(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, [hwFilters.date_from, hwFilters.date_to]);

  const fetchHwChanges = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: '50' });
      if (hwFilters.date_from) params.append('date_from', hwFilters.date_from);
      if (hwFilters.date_to) params.append('date_to', hwFilters.date_to);
      if (hwFilters.change_type) params.append('change_type', hwFilters.change_type);
      if (hwFilters.search) params.append('search', hwFilters.search);
      params.append('sort_by', hwSort.field);
      params.append('sort_dir', hwSort.dir);
      const response = await api.get(`/reports/traceability/hardware/changes?${params}`);
      setHwChanges(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [hwFilters, hwSort]);

  // ============ SOFTWARE FETCHERS ============
  const fetchSwStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (swFilters.date_from) params.append('date_from', swFilters.date_from);
      if (swFilters.date_to) params.append('date_to', swFilters.date_to);
      const response = await api.get(`/reports/traceability/software/stats?${params}`);
      setSwStats(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, [swFilters.date_from, swFilters.date_to]);

  const fetchSwChanges = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: '50' });
      if (swFilters.date_from) params.append('date_from', swFilters.date_from);
      if (swFilters.date_to) params.append('date_to', swFilters.date_to);
      if (swFilters.change_type) params.append('change_type', swFilters.change_type);
      if (swFilters.search) params.append('search', swFilters.search);
      params.append('sort_by', swSort.field);
      params.append('sort_dir', swSort.dir);
      const response = await api.get(`/reports/traceability/software/changes?${params}`);
      setSwChanges(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [swFilters, swSort]);

  // ============ PARCHES FETCHERS ============
  const fetchPStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (pFilters.date_from) params.append('date_from', pFilters.date_from);
      if (pFilters.date_to) params.append('date_to', pFilters.date_to);
      const response = await api.get(`/reports/traceability/parches/stats?${params}`);
      setPStats(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, [pFilters.date_from, pFilters.date_to]);

  const fetchPChanges = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: '50' });
      if (pFilters.date_from) params.append('date_from', pFilters.date_from);
      if (pFilters.date_to) params.append('date_to', pFilters.date_to);
      if (pFilters.change_type) params.append('change_type', pFilters.change_type);
      if (pFilters.search) params.append('search', pFilters.search);
      params.append('sort_by', pSort.field);
      params.append('sort_dir', pSort.dir);
      const response = await api.get(`/reports/traceability/parches/changes?${params}`);
      setPChanges(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [pFilters, pSort]);

  // ============ LOCALADMIN FETCHERS ============
  const fetchLaStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (laFilters.date_from) params.append('date_from', laFilters.date_from);
      if (laFilters.date_to) params.append('date_to', laFilters.date_to);
      const response = await api.get(`/reports/traceability/localadmin/stats?${params}`);
      setLaStats(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, [laFilters.date_from, laFilters.date_to]);

  const fetchLaChanges = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: '50' });
      if (laFilters.date_from) params.append('date_from', laFilters.date_from);
      if (laFilters.date_to) params.append('date_to', laFilters.date_to);
      if (laFilters.change_type) params.append('change_type', laFilters.change_type);
      if (laFilters.search) params.append('search', laFilters.search);
      params.append('sort_by', laSort.field);
      params.append('sort_dir', laSort.dir);
      const response = await api.get(`/reports/traceability/localadmin/changes?${params}`);
      setLaChanges(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [laFilters, laSort]);

  // ============ ROLES FETCHERS ============
  const fetchRStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (rFilters.date_from) params.append('date_from', rFilters.date_from);
      if (rFilters.date_to) params.append('date_to', rFilters.date_to);
      const response = await api.get(`/reports/traceability/roles/stats?${params}`);
      setRStats(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, [rFilters.date_from, rFilters.date_to]);

  const fetchRChanges = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: '50' });
      if (rFilters.date_from) params.append('date_from', rFilters.date_from);
      if (rFilters.date_to) params.append('date_to', rFilters.date_to);
      if (rFilters.change_type) params.append('change_type', rFilters.change_type);
      if (rFilters.search) params.append('search', rFilters.search);
      params.append('sort_by', rSort.field);
      params.append('sort_dir', rSort.dir);
      const response = await api.get(`/reports/traceability/roles/changes?${params}`);
      setRChanges(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [rFilters, rSort]);

  // Tab change effects
  useEffect(() => {
    if (activeTab === 'hardware') { fetchHwStats(); fetchHwChanges(1); }
  }, [activeTab, hwFilters, fetchHwStats, fetchHwChanges]);

  useEffect(() => {
    if (activeTab === 'software') { fetchSwStats(); fetchSwChanges(1); }
  }, [activeTab, swFilters, fetchSwStats, fetchSwChanges]);

  useEffect(() => {
    if (activeTab === 'parches') { fetchPStats(); fetchPChanges(1); }
  }, [activeTab, pFilters, fetchPStats, fetchPChanges]);

  useEffect(() => {
    if (activeTab === 'localadmin') { fetchLaStats(); fetchLaChanges(1); }
  }, [activeTab, laFilters, fetchLaStats, fetchLaChanges]);

  useEffect(() => {
    if (activeTab === 'roles') { fetchRStats(); fetchRChanges(1); }
  }, [activeTab, rFilters, fetchRStats, fetchRChanges]);

  // Export handler
  const handleExport = async (type) => {
    setExporting(true);
    try {
      const filtersMap = {
        hardware: hwFilters, software: swFilters, parches: pFilters,
        localadmin: laFilters, roles: rFilters
      };
      const filters = filtersMap[type];
      const params = new URLSearchParams();
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await api.get(`/reports/traceability/${type}/export?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trazabilidad_${type}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Exportación completada');
    } catch (error) {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => formatDateARG(dateStr);

  // Handle sorting for each tab
  const handleSort = (tab, field) => {
    const setters = {
      hardware: setHwSort,
      software: setSwSort,
      parches: setPSort,
      localadmin: setLaSort,
      roles: setRSort
    };
    const setter = setters[tab];
    if (setter) {
      setter(prev => ({
        field,
        dir: prev.field === field && prev.dir === 'desc' ? 'asc' : 'desc'
      }));
    }
  };

  // Get current sort config for tab
  const getSortConfig = (tab) => {
    const configs = { hardware: hwSort, software: swSort, parches: pSort, localadmin: laSort, roles: rSort };
    return configs[tab] || { field: 'fecha', dir: 'desc' };
  };

  // Get change icon for hardware
  const getHwChangeIcon = (field) => {
    const icons = { 'RAM': MemoryStick, 'OS': Monitor, 'Cores': Cpu, 'Storage': HardDrive };
    const Icon = icons[field] || AlertTriangle;
    return <Icon className="h-4 w-4" />;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Trazabilidad Servidores</h1>
            <p className="page-subtitle">Control de cambios de Hardware, Software, Parches, Admins Locales y Roles</p>
          </div>
          <Button onClick={() => handleExport(activeTab)} disabled={exporting} variant="outline" data-testid="export-btn">
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exportando...' : 'Exportar'}
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="hardware" data-testid="tab-hardware">
              <HardDrive className="h-4 w-4 mr-2" /> Hardware
              {overview.hardware.available && overview.hardware.changes_week > 0 && (
                <span className="ml-2 text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">{overview.hardware.changes_week}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="software" data-testid="tab-software">
              <Settings className="h-4 w-4 mr-2" /> Software
              {overview.software.available && overview.software.changes_week > 0 && (
                <span className="ml-2 text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">{overview.software.changes_week}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="parches" data-testid="tab-parches">
              <Package className="h-4 w-4 mr-2" /> Parches
              {overview.parches.available && overview.parches.changes_week > 0 && (
                <span className="ml-2 text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">{overview.parches.changes_week}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="localadmin" data-testid="tab-localadmin">
              <Users className="h-4 w-4 mr-2" /> Admins
              {overview.localadmin.available && overview.localadmin.changes_week > 0 && (
                <span className="ml-2 text-xs bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">{overview.localadmin.changes_week}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="roles" data-testid="tab-roles">
              <Shield className="h-4 w-4 mr-2" /> Roles
              {overview.roles.available && overview.roles.changes_week > 0 && (
                <span className="ml-2 text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">{overview.roles.changes_week}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks">
              <CalendarClock className="h-4 w-4 mr-2" /> Tareas
              {overview.scheduled_tasks?.available && overview.scheduled_tasks?.changes_week > 0 && (
                <span className="ml-2 text-xs bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded">{overview.scheduled_tasks.changes_week}</span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ============ TAB: HARDWARE ============ */}
          <TabsContent value="hardware">
            <div className="space-y-6">
              {/* Hardware Stats Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { key: '', icon: History, color: 'primary', value: hwStats.total_changes, label: 'Total Cambios' },
                  { key: 'ram', icon: MemoryStick, color: 'green-500', value: hwStats.ram_changes, label: 'RAM' },
                  { key: 'os', icon: Monitor, color: 'blue-500', value: hwStats.os_changes, label: 'OS' },
                  { key: 'cores', icon: Cpu, color: 'purple-500', value: hwStats.cores_changes, label: 'Cores' },
                  { key: 'storage', icon: HardDrive, color: 'yellow-500', value: hwStats.storage_changes, label: 'Storage' },
                  { key: 'serial', icon: AlertTriangle, color: 'red-500', value: hwStats.serial_changes, label: 'Serial' },
                ].map(({ key, icon: Icon, color, value, label }) => (
                  <div key={key || 'total'}
                    className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-${color}/50 ${hwFilters.change_type === key ? `ring-2 ring-${color}` : ''}`}
                    onClick={() => setHwFilters(prev => ({ ...prev, change_type: prev.change_type === key ? '' : key }))}>
                    <Icon className={`h-6 w-6 text-${color} flex-shrink-0`} strokeWidth={1.5} />
                    <div>
                      <div className={`stats-card-value ${key ? `text-${color}` : ''}`}>{value}</div>
                      <div className="stats-card-label">{label}</div>
                    </div>
                  </div>
                ))}
              </div>

              <FiltersBar filters={hwFilters} setFilters={setHwFilters} placeholder="Buscar servidor..." />

              <Card className="card-subtle overflow-hidden">
                <CardContent className="p-0">
                  {loading ? (
                    <LoadingSpinner />
                  ) : hwChanges.items.length === 0 ? (
                    <EmptyState error={hwChanges.error} />
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="table-subtle">
                        <thead>
                          <tr>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('hardware', 'hostname')}>
                              <div className="flex items-center gap-1">
                                SERVIDOR <SortIcon field="hostname" sortConfig={hwSort} />
                              </div>
                            </th>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('hardware', 'fecha')}>
                              <div className="flex items-center gap-1">
                                FECHA <SortIcon field="fecha" sortConfig={hwSort} />
                              </div>
                            </th>
                            <th>CAMBIOS DETECTADOS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hwChanges.items.map((item, idx) => (
                            <tr key={`${item.hostname}-${idx}`}>
                              <td className="font-mono text-primary">{item.hostname}</td>
                              <td className="text-muted-foreground text-sm">{formatDate(item.captured_at_new)}</td>
                              <td>
                                <div className="flex flex-wrap gap-2">
                                  {item.changes.map((change, cidx) => (
                                    <div key={cidx} className="inline-flex items-center gap-1.5 text-xs bg-muted/50 px-2 py-1 rounded">
                                      {getHwChangeIcon(change.field)}
                                      <span className="font-medium">{change.field}:</span>
                                      <span className="text-muted-foreground line-through">{change.old || 'N/A'}</span>
                                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                      <span className="text-primary">{change.new || 'N/A'}</span>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
              <PaginationBar data={hwChanges} onPageChange={fetchHwChanges} />
            </div>
          </TabsContent>

          {/* ============ TAB: SOFTWARE ============ */}
          <TabsContent value="software">
            <div className="space-y-6">
              <ListStatsCards stats={swStats} filters={swFilters} setFilters={setSwFilters} />
              <FiltersBar filters={swFilters} setFilters={setSwFilters} placeholder="Buscar servidor o software..." />
              <Card className="card-subtle overflow-hidden">
                <CardContent className="p-0">
                  {loading ? (
                    <LoadingSpinner />
                  ) : swChanges.items.length === 0 ? (
                    <EmptyState error={swChanges.error} icon={Settings} />
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="table-subtle">
                        <thead>
                          <tr>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('software', 'hostname')}>
                              <div className="flex items-center gap-1">
                                SERVIDOR <SortIcon field="hostname" sortConfig={swSort} />
                              </div>
                            </th>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('software', 'tipo')}>
                              <div className="flex items-center gap-1">
                                TIPO <SortIcon field="tipo" sortConfig={swSort} />
                              </div>
                            </th>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('software', 'nombre')}>
                              <div className="flex items-center gap-1">
                                SOFTWARE <SortIcon field="nombre" sortConfig={swSort} />
                              </div>
                            </th>
                            <th>VERSIÓN</th>
                            <th>PROVEEDOR</th>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('software', 'fecha')}>
                              <div className="flex items-center gap-1">
                                FECHA <SortIcon field="fecha" sortConfig={swSort} />
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {swChanges.items.map((item, idx) => (
                            <tr key={`${item.hostname}-${item.name}-${idx}`}>
                              <td className="font-mono text-primary">{item.hostname}</td>
                              <td><ChangeTypeBadge type={item.change_type} /></td>
                              <td className="font-medium">{item.name}</td>
                              <td className="text-muted-foreground">{item.version || '-'}</td>
                              <td className="text-muted-foreground text-sm">{item.providername || '-'}</td>
                              <td className="text-muted-foreground text-sm">{formatDate(item.captured_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
              <PaginationBar data={swChanges} onPageChange={fetchSwChanges} />
            </div>
          </TabsContent>

          {/* ============ TAB: PARCHES ============ */}
          <TabsContent value="parches">
            <div className="space-y-6">
              <ListStatsCards stats={pStats} filters={pFilters} setFilters={setPFilters} />
              <FiltersBar filters={pFilters} setFilters={setPFilters} placeholder="Buscar servidor o KB..." />
              <Card className="card-subtle overflow-hidden">
                <CardContent className="p-0">
                  {loading ? (
                    <LoadingSpinner />
                  ) : pChanges.items.length === 0 ? (
                    <EmptyState error={pChanges.error} icon={Package} />
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="table-subtle">
                        <thead>
                          <tr>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('parches', 'hostname')}>
                              <div className="flex items-center gap-1">
                                SERVIDOR <SortIcon field="hostname" sortConfig={pSort} />
                              </div>
                            </th>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('parches', 'tipo')}>
                              <div className="flex items-center gap-1">
                                TIPO <SortIcon field="tipo" sortConfig={pSort} />
                              </div>
                            </th>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('parches', 'id_parche')}>
                              <div className="flex items-center gap-1">
                                KB ID <SortIcon field="id_parche" sortConfig={pSort} />
                              </div>
                            </th>
                            <th>DESCRIPCIÓN</th>
                            <th>FECHA INSTALACIÓN</th>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('parches', 'fecha')}>
                              <div className="flex items-center gap-1">
                                DETECTADO <SortIcon field="fecha" sortConfig={pSort} />
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {pChanges.items.map((item, idx) => (
                            <tr key={`${item.hostname}-${item.id_parche}-${idx}`}>
                              <td className="font-mono text-primary">{item.hostname}</td>
                              <td><ChangeTypeBadge type={item.change_type} /></td>
                              <td className="font-mono">{item.id_parche}</td>
                              <td className="text-sm max-w-xs truncate" title={item.descripcion}>{item.descripcion || '-'}</td>
                              <td className="text-muted-foreground text-sm">{item.fecha_instalacion ? formatDate(item.fecha_instalacion) : '-'}</td>
                              <td className="text-muted-foreground text-sm">{formatDate(item.captured_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
              <PaginationBar data={pChanges} onPageChange={fetchPChanges} />
            </div>
          </TabsContent>

          {/* ============ TAB: LOCALADMIN (⚠️ SEGURIDAD) ============ */}
          <TabsContent value="localadmin">
            <div className="space-y-6">
              {/* Alert banner for security */}
              {laStats.added > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
                  <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-red-400">Alerta de Seguridad</p>
                    <p className="text-sm text-muted-foreground">
                      Se detectaron <span className="text-red-400 font-bold">{laStats.added}</span> nuevos administradores locales en el período. 
                      Revisá si son cambios autorizados.
                    </p>
                  </div>
                </div>
              )}

              <ListStatsCards stats={laStats} filters={laFilters} setFilters={setLaFilters} />
              <FiltersBar filters={laFilters} setFilters={setLaFilters} placeholder="Buscar servidor o usuario..." />
              <Card className="card-subtle overflow-hidden">
                <CardContent className="p-0">
                  {loading ? (
                    <LoadingSpinner />
                  ) : laChanges.items.length === 0 ? (
                    <EmptyState error={laChanges.error} icon={Users} />
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="table-subtle">
                        <thead>
                          <tr>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('localadmin', 'hostname')}>
                              <div className="flex items-center gap-1">
                                SERVIDOR <SortIcon field="hostname" sortConfig={laSort} />
                              </div>
                            </th>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('localadmin', 'tipo')}>
                              <div className="flex items-center gap-1">
                                TIPO <SortIcon field="tipo" sortConfig={laSort} />
                              </div>
                            </th>
                            <th>DOMINIO</th>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('localadmin', 'usuario')}>
                              <div className="flex items-center gap-1">
                                USUARIO <SortIcon field="usuario" sortConfig={laSort} />
                              </div>
                            </th>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('localadmin', 'fecha')}>
                              <div className="flex items-center gap-1">
                                FECHA <SortIcon field="fecha" sortConfig={laSort} />
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {laChanges.items.map((item, idx) => (
                            <tr key={`${item.hostname}-${item.user}-${idx}`} 
                              className={item.change_type === 'added' ? 'bg-red-500/5' : ''}>
                              <td className="font-mono text-primary">{item.hostname}</td>
                              <td><ChangeTypeBadge type={item.change_type} isAlert={item.change_type === 'added'} /></td>
                              <td className="text-muted-foreground">{item.domain || '-'}</td>
                              <td className={`font-medium ${item.change_type === 'added' ? 'text-red-400' : ''}`}>
                                {item.change_type === 'added' && <AlertTriangle className="h-4 w-4 inline mr-1" />}
                                {item.user}
                              </td>
                              <td className="text-muted-foreground text-sm">{formatDate(item.captured_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
              <PaginationBar data={laChanges} onPageChange={fetchLaChanges} />
            </div>
          </TabsContent>

          {/* ============ TAB: ROLES ============ */}
          <TabsContent value="roles">
            <div className="space-y-6">
              <ListStatsCards stats={rStats} filters={rFilters} setFilters={setRFilters} />
              <FiltersBar filters={rFilters} setFilters={setRFilters} placeholder="Buscar servidor o rol..." />
              <Card className="card-subtle overflow-hidden">
                <CardContent className="p-0">
                  {loading ? (
                    <LoadingSpinner />
                  ) : rChanges.items.length === 0 ? (
                    <EmptyState error={rChanges.error} icon={Shield} />
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="table-subtle">
                        <thead>
                          <tr>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('roles', 'hostname')}>
                              <div className="flex items-center gap-1">
                                SERVIDOR <SortIcon field="hostname" sortConfig={rSort} />
                              </div>
                            </th>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('roles', 'tipo')}>
                              <div className="flex items-center gap-1">
                                TIPO <SortIcon field="tipo" sortConfig={rSort} />
                              </div>
                            </th>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('roles', 'rol')}>
                              <div className="flex items-center gap-1">
                                ROL <SortIcon field="rol" sortConfig={rSort} />
                              </div>
                            </th>
                            <th>NOMBRE INTERNO</th>
                            <th>TIPO FEATURE</th>
                            <th>ESTADO</th>
                            <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('roles', 'fecha')}>
                              <div className="flex items-center gap-1">
                                FECHA <SortIcon field="fecha" sortConfig={rSort} />
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {rChanges.items.map((item, idx) => (
                            <tr key={`${item.hostname}-${item.internal_name}-${idx}`}>
                              <td className="font-mono text-primary">{item.hostname}</td>
                              <td><ChangeTypeBadge type={item.change_type} /></td>
                              <td className="font-medium">{item.role_name}</td>
                              <td className="text-muted-foreground font-mono text-sm">{item.internal_name}</td>
                              <td className="text-muted-foreground text-sm">{item.feature_type || '-'}</td>
                              <td>
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  item.install_state === 'Installed' ? 'bg-green-500/20 text-green-400' : 'bg-muted text-muted-foreground'
                                }`}>
                                  {item.install_state}
                                </span>
                              </td>
                              <td className="text-muted-foreground text-sm">{formatDate(item.captured_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
              <PaginationBar data={rChanges} onPageChange={fetchRChanges} />
            </div>
          </TabsContent>

          {/* ============ TAB: TAREAS PROGRAMADAS ============ */}
          <TabsContent value="tasks">
            <ScheduledTasksTab />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default TraceabilityReport;
